import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AlertConfigPanel = ({ userId }) => {
  const [config, setConfig] = useState({
    defaultThreshold1: 80,
    defaultThreshold2: 95,
    notificationMethods: {
      inApp: true,
      email: false,
      push: false
    },
    frequency: 'immediate', // 'immediate', 'daily', 'weekly'
    endOfMonthSummary: true
  });
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    // In a real implementation, this would save to the database
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-24 md:p-32">
      <h2 className="text-xl font-semibold text-card-foreground mb-24">Alert Configuration</h2>
      {/* Default Thresholds */}
      <div className="mb-32">
        <h3 className="text-lg font-medium text-foreground mb-16">Default Alert Thresholds</h3>
        <p className="text-sm text-muted-foreground mb-16">
          These thresholds will be applied to new budget limits
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              First Alert Threshold (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={config?.defaultThreshold1}
              onChange={(e) => setConfig({ ...config, defaultThreshold1: parseInt(e?.target?.value) })}
              className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Second Alert Threshold (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={config?.defaultThreshold2}
              onChange={(e) => setConfig({ ...config, defaultThreshold2: parseInt(e?.target?.value) })}
              className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
            />
          </div>
        </div>
      </div>
      {/* Notification Methods */}
      <div className="mb-32">
        <h3 className="text-lg font-medium text-foreground mb-16">Notification Methods</h3>
        <div className="space-y-12">
          <label className="flex items-center gap-12 cursor-pointer">
            <input
              type="checkbox"
              checked={config?.notificationMethods?.inApp}
              onChange={(e) => setConfig({
                ...config,
                notificationMethods: { ...config?.notificationMethods, inApp: e?.target?.checked }
              })}
              className="w-5 h-5 rounded border-input text-primary focus:ring-2 focus:ring-primary"
            />
            <div className="flex items-center gap-8">
              <Icon name="Bell" size={18} color="var(--color-foreground)" />
              <span className="text-sm text-foreground">In-App Notifications</span>
            </div>
          </label>
          <label className="flex items-center gap-12 cursor-pointer">
            <input
              type="checkbox"
              checked={config?.notificationMethods?.email}
              onChange={(e) => setConfig({
                ...config,
                notificationMethods: { ...config?.notificationMethods, email: e?.target?.checked }
              })}
              className="w-5 h-5 rounded border-input text-primary focus:ring-2 focus:ring-primary"
            />
            <div className="flex items-center gap-8">
              <Icon name="Mail" size={18} color="var(--color-foreground)" />
              <span className="text-sm text-foreground">Email Notifications</span>
            </div>
          </label>
          <label className="flex items-center gap-12 cursor-pointer">
            <input
              type="checkbox"
              checked={config?.notificationMethods?.push}
              onChange={(e) => setConfig({
                ...config,
                notificationMethods: { ...config?.notificationMethods, push: e?.target?.checked }
              })}
              className="w-5 h-5 rounded border-input text-primary focus:ring-2 focus:ring-primary"
            />
            <div className="flex items-center gap-8">
              <Icon name="Smartphone" size={18} color="var(--color-foreground)" />
              <span className="text-sm text-foreground">Push Notifications</span>
            </div>
          </label>
        </div>
      </div>
      {/* Notification Frequency */}
      <div className="mb-32">
        <h3 className="text-lg font-medium text-foreground mb-16">Notification Frequency</h3>
        <select
          value={config?.frequency}
          onChange={(e) => setConfig({ ...config, frequency: e?.target?.value })}
          className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
        >
          <option value="immediate">Immediate (as they occur)</option>
          <option value="daily">Daily Digest</option>
          <option value="weekly">Weekly Summary</option>
        </select>
      </div>
      {/* End of Month Summary */}
      <div className="mb-32">
        <label className="flex items-center gap-12 cursor-pointer">
          <input
            type="checkbox"
            checked={config?.endOfMonthSummary}
            onChange={(e) => setConfig({ ...config, endOfMonthSummary: e?.target?.checked })}
            className="w-5 h-5 rounded border-input text-primary focus:ring-2 focus:ring-primary"
          />
          <div>
            <span className="text-sm font-medium text-foreground block">End-of-Month Summary</span>
            <span className="text-xs text-muted-foreground">Receive comprehensive budget adherence report</span>
          </div>
        </label>
      </div>
      {/* Save Button */}
      <div className="flex items-center gap-12">
        <button
          onClick={handleSave}
          className="px-24 py-12 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium min-h-[44px]"
        >
          Save Configuration
        </button>
        {saved && (
          <span className="text-sm text-success flex items-center gap-8">
            <Icon name="CheckCircle" size={16} />
            Settings saved successfully
          </span>
        )}
      </div>
    </div>
  );
};

export default AlertConfigPanel;